import type { HttpClient } from '../http/HttpClient.ts';

export type FileCategory =
  'image' | 'video' | 'pdf' | 'spreadsheet' | 'document' | 'design' | 'other';
export type FileUseType = 'item' | 'board' | 'page';

/** The Docs tab's built-in views of the library. */
export type LibraryView =
  | 'all'
  | 'images'
  | 'pdfs'
  | 'spreadsheets'
  | 'other'
  | 'items'
  | 'boards'
  | 'pages'
  | 'unused'
  | 'uploads';

/** A file in the library, as lists show it. */
export interface LibraryFile {
  id: string;
  name: string;
  category: FileCategory;
  mime: string;
  size: number;
  version: number;
  filed: boolean;
  folderId: string | null;
  /** Where it was uploaded (null: straight into Docs). */
  origin: FileUseType | null;
  usedCount: number;
  createdBy: string | null;
  uploaderName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FileVersion {
  id: string;
  number: number;
  mime: string;
  size: number;
  uploadedBy: string | null;
  uploaderName: string | null;
  createdAt: string;
}

/** One place a file is used, or was (`removed`, with what it was as the title). */
export interface FileUse {
  type: FileUseType;
  id: string;
  title: string;
  number: number | null;
  removed: boolean;
  createdAt: string;
}

export interface FileText {
  status: 'pending' | 'working' | 'done' | 'failed' | 'none';
  text: string | null;
}

export interface FileDetail {
  file: LibraryFile & { description: string | null };
  versions: FileVersion[];
  uses: FileUse[];
}

/** The project's files library, as the Docs tab uses it. */
export class LibraryApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  #base(org: string, key: string): string {
    return `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}/files`;
  }

  /** One page of a view (pass the previous page's `next` for the one after). */
  list(
    org: string,
    key: string,
    view: LibraryView,
    before: string | null,
  ): Promise<{ files: LibraryFile[]; next: string | null }> {
    const after = before ? `&before=${encodeURIComponent(before)}` : '';
    return this.#http.get(`${this.#base(org, key)}?view=${view}${after}`);
  }

  get(org: string, key: string, id: string): Promise<FileDetail> {
    return this.#http.get(`${this.#base(org, key)}/${id}`);
  }

  update(
    org: string,
    key: string,
    id: string,
    input: { name: string } | { description: string | null },
  ): Promise<FileDetail> {
    return this.#http.patch(`${this.#base(org, key)}/${id}`, input);
  }

  /** The text read out of the current version (null until read, or when there is none). */
  text(org: string, key: string, id: string): Promise<FileText> {
    return this.#http.get(`${this.#base(org, key)}/${id}/text`);
  }

  /** Put a file in a folder of the Docs tree (null: the top). */
  place(org: string, key: string, id: string, folderId: string | null): Promise<void> {
    return this.#http.put(`${this.#base(org, key)}/${id}/folder`, { folderId });
  }

  delete(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${id}`);
  }

  restore(org: string, key: string, id: string, versionId: string): Promise<FileDetail> {
    return this.#http.post(`${this.#base(org, key)}/${id}/versions/${versionId}/restore`);
  }

  #version(org: string, key: string, id: string, versionId: string): string {
    return `${this.#base(org, key)}/${id}/versions/${versionId}/download`;
  }

  /** A version's address (to show, play or download it; each version has its own). */
  versionUrl(org: string, key: string, id: string, versionId: string): string {
    return this.#http.url(this.#version(org, key, id, versionId));
  }

  /** A version's bytes (a PDF to draw). */
  bytes(org: string, key: string, id: string, versionId: string): Promise<Blob> {
    return this.#http.blob(this.#version(org, key, id, versionId));
  }

  /** The start of a version (a preview of a long text). */
  bytesStart(
    org: string,
    key: string,
    id: string,
    versionId: string,
    bytes: number,
  ): Promise<Blob> {
    return this.#http.blobStart(this.#version(org, key, id, versionId), bytes);
  }
}
